import * as React from 'react';
import type { EndingCase } from '@chess/api-model';
import { ENDING_CASE_VIEWS, endingCaseView, type EndingCaseView } from './views.ts';
import './colors.css';
import './ending-case.css';

// The ending cases drawn the same everywhere: a swatch (legend square), a
// dot (one game, e.g. in a list or a scatter), a chip (one game's case as a
// pill, e.g. the review's title bar) and a legend (every case, with an
// optional value and label wrapper, e.g. a count and a library link).
// Presentational: data and actions come in as props.

/** The case's colour as a legend square. */
export const EndingCaseSwatch: React.FC<{ case: EndingCase }> = ({ case: c }) => (
  <span className="legend-swatch ending-case-swatch" style={{ background: endingCaseView(c).colour }} aria-hidden="true" />
);

/** The case's colour as a round dot, sized to the text. */
export const EndingCaseDot: React.FC<{ case: EndingCase; title?: string }> = ({ case: c, title }) => (
  <span className="ending-case-dot" style={{ background: endingCaseView(c).colour }} title={title} aria-hidden={title ? undefined : true} />
);

export interface EndingCaseChipProps {
  case: EndingCase;
  /** Text before the case's name, muted: "Ending:" by default. */
  prefix?: string;
  /** What a click does (e.g. go to the ending); without it the chip is plain text. */
  onOpen?(): void;
  /** Words added to the tooltip after the case's description when it opens something, e.g. "Go to the ending." */
  action?: string;
  /** The click's name for analytics (`data-track` on the button), e.g. "review.bar.ending". */
  track?: string;
}

/** One game's case as a pill: a dot in the case's colour on a faint tint of it, "Ending: Slipped". */
export const EndingCaseChip: React.FC<EndingCaseChipProps> = ({ case: c, prefix = 'Ending:', onOpen, action, track }) => {
  const view = endingCaseView(c);
  const tip = action && onOpen ? `${view.description} ${action}` : view.description;
  const inner = (
    <>
      <span className="ending-case-dot" aria-hidden="true" />
      {prefix && <span className="ending-case-chip-prefix">{prefix}</span>} {view.label}
    </>
  );
  const style = { '--ending-colour': view.colour } as React.CSSProperties;
  return onOpen ? (
    <button type="button" className="ending-case-chip" style={style} title={tip} aria-label={`${prefix} ${view.label}. ${tip}`.trim()} data-track={track} onClick={onOpen}>
      {inner}
    </button>
  ) : (
    <span className="ending-case-chip" style={style} title={tip}>
      {inner}
    </span>
  );
};

/** The chip's widest form (the longest name), not interactive: the shape a loading placeholder takes. */
export const EndingCaseChipShape: React.FC<{ prefix?: string }> = ({ prefix = 'Ending:' }) => (
  <span className="ending-case-chip" aria-hidden="true">
    <span className="ending-case-dot" />
    {prefix && <span className="ending-case-chip-prefix">{prefix}</span>} Lost on time
  </span>
);

export interface EndingCaseLegendProps {
  /** One row (`column`, a value at the right of each) or a wrapping line (`row`). */
  layout?: 'column' | 'row';
  /** The value at the right of a case, e.g. "3 · 27%"; omit for names only. */
  valueFor?(view: EndingCaseView): React.ReactNode;
  /** Wraps a case's name, e.g. in a link to its games; the name as it is by default. */
  labelFor?(view: EndingCaseView): React.ReactNode;
  /** Only these cases, still in the cases' order; every case by default. */
  cases?: readonly EndingCase[];
  className?: string;
}

/** Every case in order (or the given ones), with its swatch and name (and a value when given). */
export const EndingCaseLegend: React.FC<EndingCaseLegendProps> = ({ layout = 'column', valueFor, labelFor, cases, className }) => (
  <ul className={[layout === 'row' ? 'legend-row' : 'legend', 'ending-case-legend', className].filter(Boolean).join(' ')}>
    {ENDING_CASE_VIEWS.filter((v) => !cases || cases.includes(v.case)).map((v) => (
      <li key={v.case}>
        <span className="legend-swatch ending-case-swatch" style={{ background: v.colour }} aria-hidden="true" />
        {labelFor ? labelFor(v) : v.label}
        {valueFor && <span className="legend-value">{valueFor(v)}</span>}
      </li>
    ))}
  </ul>
);
